import type { JSX } from "react";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { useCurator } from "../useCurator.js";

export function Sent(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") ?? "/home";
  const ms = Math.max(1500, Number(searchParams.get("ms")) || 12000);
  const kind = searchParams.get("kind"); // "match" | null — меняет текст

  const isMatch = kind === "match";
  const kicker = isMatch ? "ответ в пути" : "отправлено";
  const title = isMatch ? (
    <>
      {curator.firstName} <em className="italic text-velvet">прочёл</em>.
    </>
  ) : (
    <>
      Ваш ответ <em className="italic text-velvet">в пути</em>.
    </>
  );
  const description = isMatch
    ? "Если с той стороны тоже отозвалось — мы вернёмся к вам ещё одним письмом. Иногда это быстрее, чем кажется."
    : `${curator.firstName} прочтёт и, если всё сошлось, — напишет вам ещё одно письмо. Не сразу. Так лучше.`;
  const footerMeta = isMatch ? "обычно — в течение нескольких минут" : "обычно — в течение дня";

  // авто-переход через указанный ms
  useEffect(() => {
    const t = setTimeout(() => {
      navigate(next);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [navigate, next, ms]);

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right={kicker} />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-[28px] text-center">
        <motion.span
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="h-[10px] w-[10px] rounded-full bg-accent"
          aria-hidden
        />

        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-[28px] font-display font-light leading-[0.98] tracking-[-0.028em] text-ink text-[38px]"
        >
          {title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.45 }}
          className="mt-[22px] max-w-[340px] font-display italic font-light text-[16px] leading-[1.45] text-ink/70"
        >
          {description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-[30px] flex items-center gap-[10px] editorial-meta text-ink/45"
        >
          <span className="h-px w-[22px] bg-ink/20" />
          <span>{footerMeta}</span>
          <span className="h-px w-[22px] bg-ink/20" />
        </motion.div>
      </div>

      <div className="px-[22px] pb-[24px]">
        <PrimaryButton
          onClick={() => {
            navigate(next);
          }}
        >
          {next.startsWith("/match") ? "Открыть ответ" : "В редакцию"}
        </PrimaryButton>
      </div>
    </Screen>
  );
}
